import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  useRef,
} from "react";
import { postUser } from "../helper/http";

interface ModulesProviderProps {
  children: ReactNode;
}

interface Module {
  id: string;
  name: string;
  parentId?: string | null;
  isFolder?: boolean;
  [key: string]: any;
}

interface ModulesContextType {
  modules: Module[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const defaultValue: ModulesContextType = {
  modules: [],
  loading: true,
  error: null,
  refetch: async () => {},
};

export const ModulesContext = createContext<ModulesContextType>(defaultValue);

export const ModulesProvider = ({ children }: ModulesProviderProps) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadingRef = useRef(false);

  const loadModules = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      const res = await postUser("crud/list", {
        module: "module",
        page: "1",
        per_page: "100",
        sort_direction: "+",
        sort_field: "order",
      });

      const rawModules = res.data || [];
      
      const structuredModules: Module[] = [
        {
          id: "root-folder",
          name: "Modules",
          isFolder: true,
          parentId: null,
        },
        ...rawModules.map((module: any) => ({
          ...module,
          isFolder: false,
          parentId: module.parentId || "root-folder", 
        })),
      ];

      setModules(structuredModules);
    } catch (err: any) {
      console.error("Modules load error:", err);

      console.log(err);
      setError("Failed to load modules");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    loadModules();
  }, []);

  const value = useMemo(
    () => ({
      modules,
      loading,
      error,
      refetch: loadModules,
    }),
    [modules, loading, error]
  );

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = () => useContext(ModulesContext);